import { AppSidebar } from "@/components/app-sidebar";
import { QboBanner } from "@/components/qbo-banner";
import { FloatingHelpButton } from "@/features/help/FloatingHelpButton";
import { HelpDialog } from "@/features/help/HelpDialog";
import { authClient } from "@/lib/auth-client";
import { identifyUser } from "@/lib/posthog";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

// Paths a user can reach BEFORE completing onboarding. Deliberately narrow:
// /onboarding is the wizard itself; /settings/security exposes 2FA enrollment;
// /settings/notifications exposes prefs that are part of onboarding; and
// /settings/profile lets users set their name before finishing. Anything else
// — including the Workspace section of /settings (Team / QuickBooks / Invoicing
// / Customer Mapping / Danger Zone) — requires full onboarding before it can
// be accessed.
const ONBOARDING_EXEMPT_PATHS = new Set([
	"/onboarding",
	"/settings/security",
	"/settings/notifications",
	"/settings/profile",
]);

export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ location }) => {
		const { data: session } = await authClient.getSession();
		if (!session) {
			// Carry the intended destination through the login round-trip.
			// Without this, clicking a deep link in a notification email with
			// a cold session silently lands on the dashboard instead of the
			// invoice or document the email was about.
			throw redirect({ to: "/login", search: { next: location.href } });
		}
		identifyUser(session.user.id, session.user.email);

		// Phase 5 onboarding gate. Runs in this order so the user hits the
		// right step:
		// 1. Email must be verified (Better-Auth's requireEmailVerification
		//    normally blocks sign-in, but we defend anyway).
		// 2. 2FA must be enrolled (force-2FA policy for all users).
		// 3. Full onboarding must be complete (notification prefs reviewed,
		//    optional QBO step).
		const u = session.user as typeof session.user & {
			twoFactorEnabled?: boolean;
			onboardedAt?: Date | string | null;
		};
		const path = location.pathname;

		if (!u.emailVerified && path !== "/verify-email") {
			throw redirect({ to: "/verify-email" });
		}

		if (u.twoFactorEnabled !== true && !ONBOARDING_EXEMPT_PATHS.has(path)) {
			throw redirect({ to: "/onboarding" });
		}

		if (!u.onboardedAt && !ONBOARDING_EXEMPT_PATHS.has(path)) {
			throw redirect({ to: "/onboarding" });
		}
	},
	component: AppLayout,
});

function AppLayout() {
	// Help drawer state lives at the layout level (not inside FloatingHelpButton)
	// because the layout has to apply a `pr-[480px]` right-padding on `<main>`
	// while the drawer is open — that's the layout shift that lets the user
	// keep reading the page beside the help pane. The drawer is non-modal:
	// no backdrop, no inert page, no focus trap. See HelpDialog for ESC +
	// focus-return wiring.
	const [helpOpen, setHelpOpen] = useState(false);

	return (
		<div className="flex h-screen bg-[#f8f9fb]">
			<AppSidebar />
			{/* Banner sits inside the scroll container's flex column so it spans
			    every page without each route having to opt in. */}
			<div className="flex min-w-0 flex-1 flex-col">
				<QboBanner />
				<main
					className={`flex-1 overflow-y-auto p-6 transition-[padding-right] duration-200 ease-out ${
						helpOpen ? "pr-[480px]" : ""
					}`}
				>
					<Outlet />
				</main>
			</div>
			<FloatingHelpButton open={helpOpen} onOpen={() => setHelpOpen(true)} />
			<HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
		</div>
	);
}
